import { createAsyncThunk, createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit'

import callApi from '../services/callApi.tsx'
import { FetchApiDataOptions } from '../types/reduxTypes.tsx'
import { WebThemaDto } from '../types/webTypes/WebThemaDto.tsx'

// Beispielstruktur für die Antwort
export interface WebResponse {
  data: WebThemaDto
}

// Beispielstruktur für Fehler
interface Error {
  message: string
}

// Typdefinition für den Anfangszustand
interface WebState {
  selectedNwTopic: WebThemaDto | undefined
  customTopicLoading: boolean
  fehler: string | null
  ladeStatus: 'idle' | 'loading' | 'erfolgreich' | 'fehlgeschlagen'
}

// Anfangszustand definieren
const initialState: WebState = {
  selectedNwTopic: undefined,
  customTopicLoading: false,
  fehler: null,
  ladeStatus: 'idle',
}

// Generische Funktion für API-Aufrufe
async function fetchApiData({ url, token, method, responseType, bodyData }: FetchApiDataOptions): Promise<WebResponse | Error> {
  try {
    const response = await callApi({ url, token, method, responseType, bodyData })
    if (response.status === 200) {
      return { data: response.data }
    } else {
      throw new Error('Nicht erfolgreicher Statuscode')
    }
  } catch (error) {
    if (error instanceof Error) {
      return { message: error.message }
    }
    return { message: 'Unbekannter Fehler' }
  }
}

// Nutzung der generischen Funktion in Thunks
const apiAction = (actionName: string) =>
  createAsyncThunk<WebResponse, FetchApiDataOptions, { rejectValue: Error }>(actionName, async (options, { rejectWithValue }) => {
    const result = await fetchApiData(options)
    if ('data' in result) {
      return result
    } else {
      return rejectWithValue(result)
    }
  })

export const readWebEventTopic = apiAction('readWebEventTopic')

const webSlice = createSlice({
  name: 'webSlice',
  initialState,
  reducers: {
    setCustomTopicLoading(state, action) {
      state.customTopicLoading = action.payload
    },
    setSelectedNwTopic(state, action) {
      const responseData = action.payload
      state.selectedNwTopic = responseData
    },
  },
  extraReducers: (builder) => {
    builder

      //Read Web Event Topic
      .addCase(readWebEventTopic.pending, (state) => {
        state.ladeStatus = 'loading'
      })
      .addCase(readWebEventTopic.fulfilled, (state, action: PayloadAction<WebResponse>) => {
        state.ladeStatus = 'erfolgreich'
        const responseData = action.payload.data
        state.selectedNwTopic = responseData
        state.fehler = null
      })
      .addCase(readWebEventTopic.rejected, (state, action: PayloadAction<Error | undefined, string, { arg: FetchApiDataOptions; requestId: string }, SerializedError>) => {
        state.ladeStatus = 'fehlgeschlagen'
        if (action.payload?.message) {
          state.fehler = action.payload?.message || action.error?.message || 'Unbekannter Fehler'
        }
      })
  },
})

// Exportieren der Reducer und Aktionen
export const { setCustomTopicLoading, setSelectedNwTopic } = webSlice.actions

export default webSlice.reducer
